import { memo } from "react";
import type { FloodForecastVerification, WeatherSeries } from "@chula/shared";
import { PanelHeader } from "./PanelHeader";
import { PanelState } from "./PanelState";
import { useWeatherOutlook } from "../hooks/useWeatherOutlook";

interface Props {
  apiBase: string;
}

/**
 * Campus weather OUTLOOK + the honest label on FloodDash's flood signal.
 *
 * Two rules govern everything drawn here, and both come from getting burned by
 * a haze index that read like a published grade:
 *
 * 1. EVERYTHING IN THE TOP HALF IS A FORECAST, NOT A READING. Open-Meteo is a
 *    model (ECMWF + GFS blend). /api/weather is the observed feed and it is
 *    deliberately not mixed in here. A dash is shown for "no forecast", never
 *    a 0, because 0 mm claims it will not rain and a gap does not.
 *
 * 2. THE FLOOD SIGNAL CARRIES ITS OWN PRECISION. FloodDash warns constantly and
 *    is right about 1 time in 10; it catches about 9 floods in 10 at a median
 *    ~17 h of lead. Those are its own rolling measurements, read live — they
 *    move every day, and a number baked into this file would be a lie by next
 *    week. When FloodDash is unreachable the card says so rather than
 *    rendering zeros, because "0% accurate" is a claim nobody measured.
 */

const HOURS_SHOWN = 24;
const DAYS_SHOWN = 7;

/**
 * Current Bangkok wall-clock as "YYYY-MM-DDTHH:MM", the same shape the API
 * emits. Applied as a fixed +7 because Thailand has no DST, so this is exact
 * and — unlike `new Date().toISOString()` — does not depend on the host's
 * timezone or on Date parsing of a zone-less string.
 */
function bangkokNowStamp(): string {
  return new Date(Date.now() + 7 * 3_600_000).toISOString().slice(0, 16);
}

export const WeatherOutlookPanel = memo(function WeatherOutlookPanel({ apiBase }: Props) {
  const { series, verification, isLoading, verificationLoading } = useWeatherOutlook(apiBase);

  const snap: WeatherSeries | null = series?.features?.[0] ?? null;
  const tier = series?.meta?.fallbackTier ?? null;
  const note = series?.meta?.note ?? null;

  // Lexicographic compare is valid here: both sides are Bangkok wall-clock in
  // the same fixed format, so no parsing and no offset guessing.
  const nowStamp = bangkokNowStamp();
  const nextHours = (snap?.hourly ?? []).filter((h) => h.at >= nowStamp).slice(0, HOURS_SHOWN);
  const days = (snap?.daily ?? []).slice(0, DAYS_SHOWN);
  const tomorrow = snap?.tomorrow ?? null;

  return (
    <section className="left-section">
      <PanelHeader
        title="WEATHER OUTLOOK"
        ageMinutes={series?.meta?.ageMinutes ?? null}
        fallbackTier={tier}
        source="open-meteo-forecast"
      />

      {isLoading && !snap ? (
        <PanelState kind="loading" what="campus forecast series" />
      ) : !snap ? (
        <PanelState kind="error" what="campus forecast series" source="Open-Meteo" />
      ) : (
        <>
          {/* The line that stops this being mistaken for a sensor panel. */}
          {note && <div className="outlook-disclaimer caption">{note}</div>}

          {/* TOMORROW — the question this panel was opened for. */}
          <div className="outlook-tomorrow">
            <span className="caption mono" style={{ color: "var(--text-3)", letterSpacing: "0.08em" }}>
              TOMORROW · {tomorrow?.date ?? "—"}
            </span>
            {tomorrow ? (
              <span className="outlook-tomorrow-figures mono">
                <span
                  className="outlook-tomorrow-rain"
                  style={{ color: rainColor(tomorrow.precipSumMm) }}
                  title={tomorrow.precipSumMm != null
                    ? `${tomorrow.precipSumMm.toFixed(1)} mm forecast total for the day`
                    : "daily total unavailable"}
                >
                  {mm(tomorrow.precipSumMm)}
                </span>
                <span className="caption mono outlook-tomorrow-prob">
                  {pctText(tomorrow.precipProbMaxPct)}
                </span>
                <span className="caption mono outlook-tomorrow-temp">
                  {deg(tomorrow.tMinC)}–{deg(tomorrow.tMaxC)}
                </span>
              </span>
            ) : (
              <span className="caption mono outlook-empty">Next day not in forecast window.</span>
            )}
          </div>

          {/* NEXT 24 H — every 3rd hour, so the strip stays readable in the rail. */}
          {nextHours.length > 0 ? (
            <div className="outlook-hours">
              <span className="caption mono" style={{ color: "var(--text-3)", letterSpacing: "0.08em" }}>
                NEXT 24 H · TEMP / RAIN
              </span>
              <div className="outlook-hours-row">
                {nextHours.filter((_, i) => i % 3 === 0).map((h) => (
                  <div key={h.at} className="outlook-hour" title={`${h.at} · ${h.condition} · wind ${kmh(h.windKmh)}`}>
                    <span className="caption mono outlook-hour-clock">{h.at.slice(11, 16)}</span>
                    <span className="mono outlook-hour-temp">{deg(h.tempC)}</span>
                    <span className="mono outlook-hour-rain" style={{ color: rainColor(h.precipMm) }}>
                      {mm(h.precipMm)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* 7-DAY — max temp with the day's rain total beneath it. */}
          {days.length > 0 && (
            <div className="outlook-days">
              <span className="caption mono" style={{ color: "var(--text-3)", letterSpacing: "0.08em" }}>
                7 DAY
              </span>
              <div className="outlook-days-row">
                {days.map((d) => (
                  <div key={d.date} className="outlook-day" title={`${d.date} · ${mm(d.precipSumMm)} forecast`}>
                    <span className="caption mono outlook-day-name">{d.date.slice(5)}</span>
                    <span className="mono outlook-day-temp">{deg(d.tMaxC)}</span>
                    <span className="mono outlook-day-rain" style={{ color: rainColor(d.precipSumMm) }}>
                      {mm(d.precipSumMm)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <FloodVerificationCard
        verification={verification?.features?.[0] ?? null}
        tier={verification?.meta?.fallbackTier ?? null}
        isLoading={verificationLoading}
      />
    </section>
  );
});

/**
 * FloodDash's own scorecard, shown next to the flood lens because a warning
 * rate with no precision attached is the single most misleading thing this
 * dashboard could put on screen.
 */
const FloodVerificationCard = memo(function FloodVerificationCard({
  verification,
  tier,
  isLoading,
}: {
  verification: FloodForecastVerification | null;
  tier: string | null;
  isLoading: boolean;
}) {
  return (
    // A disclosure, not a 180px card: in the weather rail this qualifies the
    // flood signal, so its headline figures sit in one line and the reading of
    // them opens on demand.
    <details className="outlook-verify">
      <summary className="caption mono">
        FLOOD SIGNAL ACCURACY · FLOODDASH
        {verification?.precisionPct != null && (
          <span className="outlook-verify-summary"> · precision {Math.round(verification.precisionPct)}%</span>
        )}
        {verification?.recallPct != null && (
          <span className="outlook-verify-summary"> · recall {Math.round(verification.recallPct)}%</span>
        )}
        {!verification && !isLoading && <span className="outlook-verify-summary"> · unavailable</span>}
      </summary>

      {isLoading ? (
        <div className="caption mono outlook-empty">Loading verification…</div>
      ) : !verification ? (
        // Absent, not zero. Rendering 0% here would be an invented claim.
        <div className="caption mono outlook-empty">
          Verification unavailable — FloodDash did not answer. Figures withheld, not guessed.
        </div>
      ) : (
        <>
          <div className="outlook-verify-row">
            <Stat label="PRECISION" value={verification.precisionPct} unit="%" />
            <Stat label="RECALL" value={verification.recallPct} unit="%" />
            <Stat label="LEAD" value={verification.leadMedianH} unit="h" />
          </div>
          <div className="caption mono outlook-verify-note">
            {verification.precisionPct != null ? (
              <>About {Math.round(verification.precisionPct)} in 100 FloodDash warnings are followed by real
                flooding; it catches roughly {Math.round(verification.recallPct ?? 0)} in 100. </>
            ) : (
              <>FloodDash reported no precision figure for this window. </>
            )}
            {verification.leadP25H != null && verification.leadP75H != null && (
              <>Lead {verification.leadP25H}–{verification.leadP75H} h.</>
            )}
          </div>
          {verification.note && <div className="caption outlook-verify-source">FloodDash: “{verification.note}”</div>}
        </>
      )}
      {tier === "unavailable" && !isLoading && verification == null && (
        <div className="caption mono outlook-empty">
          <span style={{ color: "var(--text-3)" }}>tier: unavailable</span>
        </div>
      )}
    </details>
  );
});

/** A single sourced figure. Never invents a substitute when the value is null. */
function Stat({ label, value, unit }: { label: string; value: number | null; unit: string }) {
  return (
    <div className="outlook-verify-stat">
      <span className="caption mono outlook-verify-stat-label">{label}</span>
      <span className="mono outlook-verify-stat-value">
        {value == null ? "—" : `${Math.round(value * 10) / 10}${unit}`}
      </span>
    </div>
  );
}

// ─── formatting ─────────────────────────────────────────────────────────────

function mm(v: number | null): string {
  // null must render as a dash, never "0mm". 0 mm is a real forecast value and
  // it does mean "dry" — the distinction between the two is the whole point.
  if (v == null) return "—";
  // One decimal, always. This used to round to whole mm above 9.9, which turned
  // a forecast 35.8 mm into "36mm" — losing the tenth on the one figure the
  // panel exists to report, and losing MORE precision on exactly the days
  // where the total is large enough to matter operationally. Backwards.
  return `${v.toFixed(1)}mm`;
}

function deg(v: number | null): string {
  return v == null ? "—" : `${Math.round(v)}°`;
}

function kmh(v: number | null): string {
  return v == null ? "wind unknown" : `wind ${Math.round(v)} km/h`;
}

function pctText(v: number | null): string {
  return v == null ? "" : `${Math.round(v)}% chance`;
}

/** Colour follows rain depth. A dash never gets a wet colour. */
function rainColor(v: number | null): string {
  if (v == null) return "var(--text-3)";
  if (v >= 25) return "var(--bad)";
  if (v >= 10) return "var(--neg)";
  if (v >= 1) return "var(--warn)";
  return "var(--text-2)";
}